import React, { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { MessageCircle } from 'lucide-react';
import { ApiUrlContext } from "../App";

function CourseDetail() {
  const { id } = useParams();
  const apiUrl = useContext(ApiUrlContext);
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Kurs detayını çek
        const courseResponse = await fetch(`${apiUrl}/courses/${id}`);
        const courseData = await courseResponse.json();
        setCourse(courseData);

        // Kursun derslerini çek
        const lessonsResponse = await fetch(`${apiUrl}/courses/${id}/lessons`);
        if (lessonsResponse.ok) {
          const lessonsData = await lessonsResponse.json();
          setLessons(lessonsData);
        }
      } catch (error) {
        console.error("Veri yüklenirken hata:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, id]);

  if (loading) return (
    <div style={styles.loading}>
      <div style={styles.spinner}></div>
      <p>Yükleniyor...</p>
    </div>
  );
  
  if (!course) return (
    <div style={styles.error}>
      <p>Kurs bulunamadı.</p>
      <Link to="/courses" style={styles.backLink}>
        ← Kurslara Dön
      </Link>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* Üst Başlık */}
      <div style={styles.header}>
        <h1 style={styles.title}>{course.title}</h1>
        <Link 
          to={`/blog/${id}`}
          style={styles.forumButton}
        >
          <MessageCircle size={18} className="inline-icon" /> Forum & Sorular
        </Link>
      </div>

      {/* Kurs Bilgileri */}
      <div style={styles.courseInfo}>
        <div style={styles.instructorInfo}>
          <span style={styles.instructorLabel}>Eğitmen:</span>
          <span style={styles.instructorName}>{course.instructor}</span>
        </div>
        <p style={styles.description}>{course.description}</p>
      </div>

      {/* Dersler Başlığı */}
      <div style={styles.lessonsHeader}>
        <h2 style={styles.lessonsTitle}>
          Dersler {lessons.length > 0 && `(${lessons.length})`}
        </h2>
      </div>

      {/* Dersler Listesi */}
      {lessons.length === 0 ? (
        <div style={styles.emptyState}>
          Bu kursa henüz ders eklenmemiş.
        </div>
      ) : (
        <div style={styles.lessonsList}>
          {lessons
            .sort((a, b) => a.order - b.order)
            .map((lesson, index) => (
            <div key={lesson._id} style={styles.lessonCard}>
              <div style={styles.lessonContent}>
                <div style={styles.lessonNumber}>
                  {lesson.order || index + 1}
                </div>
                <div style={styles.lessonDetails}>
                  <h3 style={styles.lessonTitle}>{lesson.title}</h3>
                  {lesson.description && (
                    <p style={styles.lessonDescription}>{lesson.description}</p>
                  )}
                </div>
              </div>
              <div style={styles.lessonMeta}>
                {lesson.duration && (
                  <span style={styles.duration}>
                    ⏱️ {lesson.duration}
                  </span>
                )}
                {lesson.videoUrl && (
                  <a
                    href={lesson.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.videoLink}
                  >
                    Video
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '32px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    color: '#666',
    fontSize: '14px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #42a947',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  error: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#333',
  },
  backLink: {
    display: 'inline-block',
    marginTop: '16px',
    padding: '8px 16px',
    background: 'transparent',
    color: '#666',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    paddingBottom: '20px',
    borderBottom: '1px solid #eee',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0',
    flex: 1,
  },
  forumButton: {
    background: 'transparent',
    color: '#666',
    padding: '10px 20px',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    border: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    marginLeft: '20px',
  },
  courseInfo: {
    marginBottom: '32px',
  },
  instructorInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  instructorLabel: {
    color: '#666',
    fontSize: '14px',
    fontWeight: '500',
  },
  instructorName: {
    color: '#333',
    fontSize: '14px',
    fontWeight: '400',
  },
  description: {
    color: '#555',
    fontSize: '16px',
    lineHeight: '1.6',
    margin: '0',
  },
  lessonsHeader: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #eee',
  },
  lessonsTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    margin: '0',
  },
  emptyState: {
    textAlign: 'center',
    color: '#666',
    padding: '60px 20px',
    background: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #eee',
    fontSize: '14px',
  },
  lessonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  lessonCard: {
    background: '#fff',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'all 0.2s ease',
  },
  lessonContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  lessonNumber: {
    background: '#f8f9fa',
    color: '#333',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '500',
    border: '1px solid #e0e0e0',
  },
  lessonDetails: {
    flex: 1,
  },
  lessonTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#333',
    margin: '0 0 4px 0',
  },
  lessonDescription: {
    fontSize: '14px',
    color: '#666',
    margin: '0',
    lineHeight: '1.5',
  },
  lessonMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  duration: {
    color: '#666',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  videoLink: {
    color: '#10B981',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    textDecoration: 'none',
    fontWeight: '500',
  },
};

// CSS Animasyonu
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  a:hover {
    background: #f8f9fa;
    border-color: #ced4da;
  }
  
  .lessonCard:hover {
    border-color: #e0e0e0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }
`;
document.head.appendChild(styleSheet);

export default CourseDetail;
